import { formatClockDate, formatClockTime, useClock } from "../../lib/use-clock";

interface CoachIdentityProps {
  coachName: string;
  imageUrl: string | null;
  onEdit?: () => void;
}

/**
 * Identity block: large avatar + dramatic greeting + live clock & date chips.
 * Avatar is a tap target for edit-coach. Clock updates every 30s.
 */
export function CoachIdentity({ coachName, imageUrl, onEdit }: CoachIdentityProps) {
  const now = useClock();
  const time = formatClockTime(now);
  const date = formatClockDate(now);
  const trimmed = coachName.trim();
  const hasRealName = trimmed.length > 0 && trimmed.toLowerCase() !== "coach";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const firstName = parts[0] || "Coach";
  // Initials: first + last name letter when we have at least two parts,
  // otherwise the first two letters of the only name, falling back to "C".
  const initials = hasRealName
    ? parts.length >= 2
      ? (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
      : parts[0]!.slice(0, 2).toUpperCase()
    : "C";
  // Greeting: when we have a real name show "Coach <Vorname>.", otherwise
  // keep the neutral "Coach." fallback so we don't say "Coach Coach.".
  const greeting = hasRealName ? `Coach ${firstName}.` : "Coach.";

  return (
    <section className="home-identity-row relative z-10 flex items-center gap-5 py-3">
      <button
        type="button"
        onClick={onEdit}
        aria-label="Coach bearbeiten"
        className="group relative shrink-0 rounded-full focus-visible:outline-none"
        style={{
          width: 112,
          height: 112,
          border: "2px solid rgba(255,255,255,0.18)",
          background: "linear-gradient(160deg, rgba(151,252,0,0.18) 0%, rgba(0,122,255,0.10) 100%)",
          boxShadow: "0 18px 40px -16px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.18)",
          overflow: "hidden",
        }}
      >
        {imageUrl ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img src={imageUrl} className="size-full object-cover" style={{ display: "block" }} />
        ) : (
          <span
            className="home-display flex size-full items-center justify-center"
            style={{
              fontSize: 48,
              fontWeight: 800,
              color: "#f4f5f3",
              textShadow: "0 2px 12px rgba(0,0,0,0.4)",
            }}
          >
            {initials}
          </span>
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div
          className="text-[12px] uppercase tracking-[0.22em]"
          style={{ color: "var(--color-pitch-lime)" }}
        >
          Servus
        </div>
        <h1 className="home-display home-greeting mt-1 truncate landscape:overflow-visible landscape:whitespace-normal landscape:text-clip landscape:wrap-break-word">
          {greeting}
        </h1>
        <div
          className="home-display mt-2 text-[18px]"
          style={{ color: "var(--color-on-surface-variant)", fontWeight: 500 }}
        >
          Bereit für den Spieltag?
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div
            className="tabular-nums home-display flex items-center gap-2 rounded-full px-3 py-1.5"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.10)",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "-0.01em",
            }}
          >
            <span
              className="inline-block size-1.5 rounded-full"
              style={{
                background: "var(--color-pitch-lime)",
                boxShadow: "0 0 8px var(--color-pitch-lime)",
              }}
              aria-hidden
            />
            {time}
          </div>
          <div
            className="rounded-full px-3 py-1.5 text-[13px]"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "var(--color-on-surface-variant)",
              fontWeight: 500,
            }}
          >
            {date}
          </div>
        </div>
      </div>
    </section>
  );
}
